// biome-ignore-all lint/correctness/useUniqueElementIds: historic
//
// Dedicated layout for the customize-card onboarding step. 39% cream side
// pane / 61% cardWhite main pane with a 40px inside-edge corner radius and
// a sticky bottom navigation row. Applies `rebrandTheme` to its subtree so
// ui-lib components inside pick up rebrand tokens.
import { BlinqLogo } from '@components/BlinqAssets';
import Tooltip, { centeredAbove } from '@components/Tooltip/Tooltip';
import useMedia from '@hooks/use-media';
import { noop } from '@modules/helpers';
import { makePageTitle } from '@modules/page-title';
import { Button } from '@ui-lib';
import classNames from 'classnames';
import Head from 'next/head';
import type { FC, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';
import { useState } from 'react';

import * as styles from './CustomizeCardLayout.css';
import LegalFooterDisclaimerComponent from './LegalFooterDisclaimer';

type SidePosition = 'left' | 'right';

interface CustomizeCardLayoutProps {
  title: string;
  sideSection?: SidePosition;
  children: ReactNode;
}

const CustomizeCardLayout: FC<CustomizeCardLayoutProps> = ({
  children,
  title,
  sideSection = 'left',
}) => {
  const pageTitle = makePageTitle(title);

  return (
    <div className={styles.root}>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <div
        style={{
          display: 'flex',
          flex: '1 1 auto',
          minHeight: 0,
          flexDirection: sideSection === 'right' ? 'row-reverse' : 'row',
        }}
        data-side-section={sideSection}
      >
        {children}
      </div>
    </div>
  );
};

// --- Sections ---------------------------------------------------------------

const SideSection: FC<
  PropsWithChildren<{ theme?: 'light' | 'dark'; hideLogo?: boolean; allowOverflow?: boolean }>
> = ({ hideLogo = false, children }) => (
  <aside className={styles.side}>
    {!hideLogo && (
      <div className={styles.logoContainer}>
        <BlinqLogo className={styles.blinqLogo} />
      </div>
    )}
    <div className={styles.sideContent}>{children}</div>
  </aside>
);

// MainSection accepts an optional `sidePosition` so the inside corner-
// radius lands on the correct edge. Defaults to `left` which matches the
// most common layout.
const MainSection = ({
  ref: forwardedRef,
  sidePosition = 'left',
  children,
}: {
  children: ReactNode;
  sidePosition?: SidePosition;
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <main className={classNames(styles.main[sidePosition], styles.mainPadding)} ref={forwardedRef}>
    {children}
  </main>
);
MainSection.displayName = 'MainSection';

const MainSectionContents: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <div className={styles.mainContents}>{children}</div>
);

interface MainSectionHeaderProps {
  title: string;
  explainer?: ReactNode;
}

const MainSectionHeader: FC<PropsWithChildren<MainSectionHeaderProps>> = ({ title, explainer }) => (
  <div className={styles.mainHeader}>
    <h1 className={styles.mainHeaderHeading}>{title}</h1>
    {explainer && <p className={styles.mainHeaderExplainer}>{explainer}</p>}
  </div>
);

interface NavigationButtonsSectionProps {
  sidePosition?: SidePosition;
  /**
   * Below tablet, stack the disclaimer above the actions instead of squeezing
   * them onto one row. Used where the row carries more than one button.
   */
  stackOnMobile?: boolean;
}

const NavigationButtonsSection: FC<PropsWithChildren<NavigationButtonsSectionProps>> = ({
  sidePosition = 'left',
  stackOnMobile = false,
  children,
}) => (
  <div
    className={classNames(
      styles.navigationButtons[sidePosition],
      stackOnMobile && styles.navigationButtonsStacked,
    )}
  >
    {children}
  </div>
);

// Groups the buttons at the end of the navigation row. On a stacked row the
// buttons share the width equally.
const NavigationActions: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <div className={styles.navigationActions}>{children}</div>
);

// --- Buttons ----------------------------------------------------------------

interface ForwardButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}

const ForwardButton: FC<PropsWithChildren<ForwardButtonProps>> = ({
  children,
  onClick,
  loading,
  fullWidth = false,
  disabled = false,
}) => (
  <div className={styles.forwardButtonWrap} data-full-width={fullWidth}>
    <Button
      variant="solid"
      size="regular"
      type="submit"
      loading={loading}
      onClick={onClick ?? noop}
      disabled={disabled}
      width={fullWidth ? 'full' : undefined}
    >
      {children}
    </Button>
  </div>
);

interface BackButtonProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
}

const BackButton: FC<PropsWithChildren<BackButtonProps>> = ({ children, onClick, loading }) => {
  const buttonMode = useMedia<'small' | 'regular'>([`(max-width: 350px)`], ['small'], 'regular');
  return (
    <Button variant="text" size={buttonMode} onClick={onClick} loading={loading}>
      {children}
    </Button>
  );
};

interface CustomizeCardForwardButtonProps extends ForwardButtonProps {
  /** Why the button is disabled, shown as a tooltip on hover. */
  disabledReason?: string;
}

// Customize-card variant — wraps the forward button with a tooltip that
// explains why it's disabled until the user adds a field.
const CustomizeCardForwardButton: FC<PropsWithChildren<CustomizeCardForwardButtonProps>> = ({
  children,
  onClick,
  loading,
  fullWidth = false,
  disabled = false,
  disabledReason = 'Add one or more details to continue',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div
      className={styles.forwardButtonWrap}
      data-full-width={fullWidth}
      onMouseEnter={() => {
        if (disabled) setShowTooltip(true);
      }}
    >
      <Tooltip
        size="small"
        label={disabledReason}
        aria-label="Save button disabled message"
        show={showTooltip}
        showTriangle={false}
        positioner={centeredAbove}
      >
        <div>
          <Button
            variant="solid"
            size="regular"
            type="submit"
            loading={loading}
            onClick={onClick ?? noop}
            disabled={disabled}
            width={fullWidth ? 'full' : undefined}
          >
            {children}
          </Button>
        </div>
      </Tooltip>
    </div>
  );
};

// --- Footer + disclaimer ----------------------------------------------------

interface OnboardingFooterProps {
  leading?: ReactNode;
  center?: ReactNode;
  trailing?: ReactNode;
}

const OnboardingFooter: FC<OnboardingFooterProps> = ({ leading, center, trailing }) => (
  <div className={styles.footer}>
    <div className={styles.footerSlot.leading}>{leading}</div>
    <div className={styles.footerSlot.center}>{center}</div>
    <div className={styles.footerSlot.trailing}>{trailing}</div>
  </div>
);

const LegalFooterDisclaimer: FC = () => (
  <div className={styles.legalDisclaimer}>
    <LegalFooterDisclaimerComponent />
  </div>
);

export default CustomizeCardLayout;
export {
  BackButton,
  CustomizeCardForwardButton,
  ForwardButton,
  LegalFooterDisclaimer,
  MainSection,
  MainSectionContents,
  MainSectionHeader,
  NavigationActions,
  NavigationButtonsSection,
  OnboardingFooter,
  SideSection,
};
